import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@shared/api';
import { createLogger } from '@shared/lib';
import type { User } from '@entities/user';
import type { UserRole } from '@cockpit/types/user';

import { DEFAULT_ROLE } from '../lib';

const log = createLogger('AuthService');

// Collection path for users
const COLLECTION_PATH = 'users';

/**
 * Transforms Firestore document to User
 */
function transformDoc(id: string, data: Record<string, unknown>): User {
  return {
    id,
    ...data,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  } as User;
}

/**
 * Service for user authentication and Firestore user documents
 *
 * Note: Firebase Auth operations (signIn, signOut) are in firebase.ts
 * This service handles the Firestore user document CRUD operations
 */
export const AuthService = {
  /**
   * Fetches a user document by ID (Firebase Auth UID)
   */
  async getUser(uid: string): Promise<User | null> {
    try {
      const docRef = doc(db, COLLECTION_PATH, uid);
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        return null;
      }

      return transformDoc(snapshot.id, snapshot.data());
    } catch (error) {
      log.error('Error getting user:', error);
      throw error;
    }
  },

  /**
   * Creates a new user document (called on first login)
   *
   * ⚠ `role` kommt aus den Custom Claims des Tokens, nicht aus dem Nichts.
   * Bis zum 10.09.2026 stand hier fest `'employee'`, und das hatte eine
   * stille Folge: Fehlte einem Admin sein Dokument — gelöscht, nie angelegt,
   * ein zurückgesetzter Emulator —, dann legte die nächste Anmeldung eins an
   * und machte ihn zum Mitarbeiter. Ohne Fehler, ohne Meldung; man merkt es
   * daran, dass Schaltflächen fehlen.
   *
   * Ein Selbst-Aufstieg ist das nicht: Claims setzt nur der Server, die
   * Weißliste in `roleFromClaims` lässt nur bekannte Rollen durch, und
   * `firestore.rules` prüft dasselbe noch einmal gegen dasselbe Token.
   *
   * @param uid - Firebase Auth UID
   * @param email - User email from Firebase Auth
   * @param displayName - User display name from Firebase Auth
   * @param role - Rolle aus den Custom Claims; ohne Marke `employee`
   * @returns The created user document
   */
  async createUser(
    uid: string,
    email: string,
    displayName: string,
    role: UserRole = DEFAULT_ROLE
  ): Promise<User> {
    try {
      const docRef = doc(db, COLLECTION_PATH, uid);

      const userData = {
        email,
        displayName,
        role,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(docRef, userData);

      // Fetch the created document to get actual timestamps
      const createdDoc = await getDoc(docRef);
      if (!createdDoc.exists()) {
        throw new Error('Failed to create user document');
      }

      return transformDoc(uid, createdDoc.data());
    } catch (error) {
      log.error('Error creating user:', error);
      throw error;
    }
  },

  /**
   * Updates a user document
   */
  async updateUser(
    uid: string,
    data: Partial<Omit<User, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_PATH, uid);

      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      log.error('Error updating user:', error);
      throw error;
    }
  },

  /**
   * Real-time subscription for a user document
   *
   * @param uid - Firebase Auth UID
   * @param callback - Called with updated user data
   * @returns Unsubscribe function
   */
  subscribeToUser(
    uid: string,
    callback: (user: User | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const docRef = doc(db, COLLECTION_PATH, uid);

    return onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          callback(null);
          return;
        }

        const user = transformDoc(snapshot.id, snapshot.data());
        callback(user);
      },
      (error) => {
        log.error('Subscription error:', error);
        onError?.(error);
      }
    );
  },
};
